// Builds an email-client-safe signature: tables + inline styles, Arial (web fonts don't load in Outlook/Gmail).
const esc = (s) => String(s ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
const tel = (s) => String(s).replace(/[^\d+]/g, "");
const href = (s) => (/^https?:\/\//i.test(s) ? s : `https://${s}`);
// Invisible zero-width non-joiners inside numbers and after spaces stop Gmail/Apple Mail turning the address into a maps link.
const noAutoLink = (s) => esc(s).replace(/(\d)(?=\d)/g, "$1&zwnj;").replace(/ /g, " &zwnj;").replace(/,/g, "&zwnj;,");

const INK = "#0A1B2E";
const BLUE = "#0369A1";
const MUTED = "#475569";
const FONT = "font-family:Arial,Helvetica,sans-serif;";

export const SIG_LOGOS = {
  3: { label: "Icon (beside details)", w: 64, h: 64, beside: true },
  4: { label: "Legal logo, ABN + ACN", w: 300, h: 96 },
  5: { label: "Legal logo, ABN only", w: 300, h: 98 },
  1: { label: "Logo on navy", w: 220, h: 76 },
  2: { label: "White logo (dark signatures only)", w: 220, h: 76 },
};

export const DEFAULT_SIG = {
  name: "", title: "", phone: "", mobile: "", email: "",
  website: "aerosoftsolutions.com.au", company: "Aero Soft Solutions Pty Ltd",
  address: "Level 1, 63-67 Ann St, Surry Hills NSW 2010", logo: "3", note: "",
};

export const buildSignature = (v, origin) => {
  const logo = SIG_LOGOS[v.logo] ?? SIG_LOGOS[3];
  const legal = v.logo === "4" || v.logo === "5";
  const note = v.note?.trim() ? esc(v.note.trim()).replace(/\r?\n/g, "<br>") : "";
  const img = `<img src="${esc(`${origin}/signature/aerosoftsolutions${v.logo}.png`)}" width="${logo.w}" height="${logo.h}" alt="Aero Soft Solutions" style="display:block;border:0;outline:none;width:${logo.w}px;height:${logo.h}px;">`;
  const link = (url, text) => `<a href="${esc(url)}" style="color:${BLUE};text-decoration:none;">${esc(text)}</a>`;
  const contacts = [
    v.phone && `<span style="color:${MUTED};">P</span>&nbsp;${link(`tel:${tel(v.phone)}`, v.phone)}`,
    v.mobile && `<span style="color:${MUTED};">M</span>&nbsp;${link(`tel:${tel(v.mobile)}`, v.mobile)}`,
    v.email && `<span style="color:${MUTED};">E</span>&nbsp;${link(`mailto:${v.email.trim()}`, v.email.trim())}`,
    v.website && `<span style="color:${MUTED};">W</span>&nbsp;${link(href(v.website.trim()), v.website.trim().replace(/^https?:\/\//i, ""))}`,
  ].filter(Boolean);
  const rows = [
    `<tr><td style="${FONT}font-size:16px;line-height:20px;font-weight:bold;color:${INK};padding:0;">${esc(v.name || "Your Name")}</td></tr>`,
    v.title && `<tr><td style="${FONT}font-size:13px;line-height:18px;color:${MUTED};padding:2px 0 0;">${esc(v.title)}</td></tr>`,
    contacts.length && `<tr><td style="${FONT}font-size:12px;line-height:18px;color:${INK};padding:8px 0 0;">${contacts.join(`&nbsp;&nbsp;<span style="color:#BAE6FD;">|</span>&nbsp;&nbsp;`)}</td></tr>`,
    (v.company || v.address) && `<tr><td style="${FONT}font-size:11px;line-height:16px;color:${MUTED};padding:6px 0 0;">${[v.company && `<b style="color:${INK};">${esc(v.company)}</b>`, v.address && `<span style="color:${MUTED};text-decoration:none;">${noAutoLink(v.address)}</span>`].filter(Boolean).join(" &middot; ")}</td></tr>`,
    note && !legal && `<tr><td style="${FONT}font-size:10px;line-height:14px;color:${MUTED};padding:8px 0 0;max-width:460px;">${note}</td></tr>`,
  ].filter(Boolean).join("");
  const details = `<table cellpadding="0" cellspacing="0" border="0" role="presentation" style="border-collapse:collapse;">${rows}</table>`;
  const body = logo.beside
    ? `<tr><td valign="top" style="padding:0 14px 0 0;">${img}</td><td valign="top" style="padding:0 0 0 14px;border-left:2px solid #BAE6FD;">${details}</td></tr>`
    : `<tr><td style="padding:0 0 10px;">${details}</td></tr><tr><td style="padding:10px 0 ${note && legal ? 10 : 0}px;border-top:1px solid #BAE6FD;">${img}</td></tr>`;
  // Legal logos: the extra line sits at the very bottom, centred across the full signature width.
  const foot = note && legal
    ? `<tr><td align="center" style="${FONT}font-size:10px;line-height:14px;color:${MUTED};text-align:center;padding:10px 0 0;border-top:1px solid #BAE6FD;">${note}</td></tr>`
    : "";
  return `<table cellpadding="0" cellspacing="0" border="0" role="presentation" style="border-collapse:collapse;">${body}${foot}</table>`;
};

export const signaturePlainText = (v) => [
  v.name || "Your Name", v.title, [v.phone && `P ${v.phone}`, v.mobile && `M ${v.mobile}`].filter(Boolean).join("  |  "),
  [v.email && `E ${v.email}`, v.website && `W ${v.website}`].filter(Boolean).join("  |  "),
  [v.company, v.address].filter(Boolean).join(" · "),
  v.note?.trim(),
].filter(Boolean).join("\n");
